import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ClipboardCheck } from 'lucide-react';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { ResultReport } from 'components/result/ResultReport';
import { useIsMobile } from 'hooks/use-media-query';
import * as ResultModule from 'modules/result';
import { FeedbackCard } from 'modules/feedback';
import { ResultRow } from './ResultRow';

function ListSkeleton() {
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex min-h-16 items-center gap-3 px-4 md:px-5">
          <Skeleton className="h-4 w-10" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className={i % 2 ? 'h-3.5 w-2/5' : 'h-3.5 w-1/2'} />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-5 w-14" />
        </div>
      ))}
    </div>
  );
}

/**
 * Master-detail over the shared `ResultReport` on desktop; phones list the
 * results and open each one on its own page. The backend scopes results to the caller.
 */
export const Results: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    items,
    isLoading,
    isError,
    refetch,
  } = ResultModule.Hooks.useList({ params: { perPage: 50 } });

  const [selectedId, setSelectedId] = useState('');
  useEffect(() => {
    // A running attempt has no reviewable result, so it can never be the default pick.
    const firstOpenable = items.find((r) => r.status !== 'in_progress');
    if (!selectedId && firstOpenable) setSelectedId(firstOpenable.id);
  }, [items, selectedId]);

  const {
    item: detail,
    isLoading: detailLoading,
    isError: detailError,
    refetch: refetchDetail,
    isFetching: detailFetching,
  } = ResultModule.Hooks.useSingle({
    id: selectedId,
    enabled: Boolean(selectedId) && !isMobile,
  });

  const completed = items.filter((r) => r.status === 'completed');
  const hasOpenable = items.some((r) => r.status !== 'in_progress');
  // IELTS/SAT results are bands or scaled scores, not percentages: only regular
  // finished tests feed the average.
  const regular = completed.filter((r) => !r.examScore);
  const avg = regular.length > 0
    ? Math.round(regular.reduce((sum, r) => sum + r.percentage, 0) / regular.length)
    : null;

  if (isError) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-40" />
          <ListSkeleton />
        </div>
        {!isMobile && <Skeleton className="h-96 rounded-lg" />}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          icon={<ClipboardCheck size={28} />}
          title={t('portal.no_results_title')}
          description={t('portal.no_results_desc')}
        />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-base font-bold text-foreground">{t('result.your_results')}</h2>
          {avg !== null && (
            <span className="text-[12.5px] text-muted-foreground">
              {t('portal.term_average')} <span className="font-semibold tabular-nums text-foreground">{avg}%</span>
            </span>
          )}
        </div>
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {items.map((r) => (
            <li key={r.id}>
              <ResultRow result={r} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 className="text-[17px] font-bold text-foreground">{t('result.your_results')}</h2>
          <span className="text-[13px] text-muted-foreground">
            {avg !== null
              ? t('result.list_summary', { count: completed.length, avg })
              : t('portal_results_completed_count', { count: completed.length })}
          </span>
        </div>
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {items.map((r) => (
            <li key={r.id}>
              <ResultRow result={r} selected={r.id === selectedId} onSelect={() => setSelectedId(r.id)} />
            </li>
          ))}
        </ul>
      </div>

      <section className="min-w-0 rounded-lg border border-border bg-card px-5 py-5 md:px-[22px] lg:sticky lg:top-20">
        {!selectedId && !hasOpenable ? (
          // Every attempt is still running: nothing is reviewable yet.
          <EmptyState
            icon={<ClipboardCheck size={26} />}
            title={t('portal.no_results_title')}
            description={t('portal.no_results_desc')}
          />
        ) : detailError ? (
          // detailLoading flips false on error too — without this branch the
          // skeleton below would spin forever (`!detail` never becomes false).
          <EmptyState
            icon={<AlertCircle size={26} />}
            title={t('failed_to_load')}
            secondaryAction={{ label: t('retry'), onClick: () => refetchDetail() }}
          />
        ) : detailLoading || !detail || detail.id !== selectedId ? (
          <div className="flex flex-col gap-3" aria-hidden="true">
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="mt-3 h-11 w-28" />
            <Skeleton className="mt-3 h-16 rounded-md" />
            <Skeleton className="h-24 rounded-md" />
          </div>
        ) : (
          <>
            <ResultReport
              data={detail}
              onReviewAnswers={() => navigate(`/portal/results/${selectedId}`)}
              onRefresh={() => refetchDetail()}
              refreshing={detailFetching}
            />
            {/* Where a learner who skipped the prompt after the test can still leave their feedback. */}
            {detail.status !== 'in_progress' && <FeedbackCard attemptId={detail.id} className="mt-5" />}
          </>
        )}
      </section>
    </div>
  );
};

export default Results;
